import type { SVGProps } from 'react';

import { PATHS, type IconName } from '@/components/ui/icon-paths';
import { cn } from '@/lib/cn';

/* Re-exported so consumers can import the glyph name alongside the component.
   Type-only, so it does not affect Fast Refresh. */
export type { IconName } from '@/components/ui/icon-paths';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Rendered size in pixels. Defaults to 16 - the density this UI is built at. */
  size?: number;
  /** Supply when the icon is the only content of a control; omit when decorative. */
  label?: string;
}

/**
 * Renders a glyph from the in-house set. Paths live in `icon-paths.ts` so this
 * module exports only a component and stays Fast Refresh friendly.
 */
export function Icon({ name, size = 16, label, className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      {...props}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
